import { toast } from "react-toastify";
import axios from "axios";
import { getDatabase, ref, get } from "firebase/database";
import { savePostToFirebase, countSavedPosts } from "./firebaseUtils";
import {
  DEFAULT_POST_LENGTH,
  DEFAULT_POST_STYLES,
} from "../../constants/postSettings";

/**
 * Đọc nội dung file văn bản
 * @param {File} file - File văn bản cần đọc
 * @returns {Promise<string>} - Nội dung file văn bản
 */
export const readTextFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};

/**
 * Xử lý các file được chọn hoặc kéo thả
 * @param {Array} files - Danh sách các file
 * @param {Array} contextFiles - Danh sách các file ngữ cảnh hiện tại
 * @param {Function} setContextFiles - Hàm cập nhật danh sách file ngữ cảnh
 * @param {Function} setIsProcessingContext - Hàm cập nhật trạng thái xử lý ngữ cảnh
 */
export const handleFiles = async (
  files,
  contextFiles,
  setContextFiles,
  setIsProcessingContext
) => {
  if (files.length === 0) return;

  setIsProcessingContext(true);

  try {
    // Kiểm tra kích thước file
    const maxSingleFileSize = 2 * 1024 * 1024; // 2MB cho mỗi file
    const maxTotalSize = 10 * 1024 * 1024; // 10MB tổng cộng

    // Tính tổng kích thước hiện tại của các file đã có
    let currentTotalSize = 0;
    contextFiles.forEach((file) => {
      if (file.file && file.file.size) {
        currentTotalSize += file.file.size;
      }
    });

    // Tính tổng kích thước của các file mới
    let newFilesTotalSize = 0;
    files.forEach((file) => {
      newFilesTotalSize += file.size;
    });

    // Kiểm tra nếu tổng kích thước vượt quá giới hạn
    if (currentTotalSize + newFilesTotalSize > maxTotalSize) {
      toast.warning(
        `Tổng kích thước file (${(
          (currentTotalSize + newFilesTotalSize) /
          (1024 * 1024)
        ).toFixed(
          2
        )}MB) vượt quá giới hạn 10MB. Một số file có thể không được tải lên.`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    }

    // Kiểm tra từng file có kích thước quá lớn không
    const oversizedFiles = files.filter(
      (file) => file.size > maxSingleFileSize
    );
    if (oversizedFiles.length > 0) {
      toast.warning(
        `Một số file có kích thước quá lớn (>2MB). Hệ thống sẽ cố gắng tự động nén ảnh.`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    }

    // Xử lý từng file
    const processedFiles = await Promise.all(
      files.map(async (file) => {
        // Xác định loại file
        const fileType = file.type.startsWith("image/") ? "image" : "text";

        // Đối với file ảnh, tạo preview
        let preview = null;
        if (fileType === "image") {
          try {
            // Tạo URL cho preview ảnh
            preview = URL.createObjectURL(file);

            // Kiểm tra kích thước ảnh
            const imgDimensions = await new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                resolve({
                  width: img.width,
                  height: img.height,
                });
              };
              img.src = preview;
            });

            // Ghi log kích thước ảnh
            console.log(`Kích thước ảnh ${file.name}:`, imgDimensions);
          } catch (error) {
            console.error(`Lỗi khi tạo preview cho file ${file.name}:`, error);
            // Nếu không tạo được preview, vẫn tiếp tục xử lý file
          }
        } else if (fileType === "text") {
          // Đối với file văn bản, đọc nội dung
          const text = await readTextFile(file);
          if (text.length > 100000) {
            // Nếu văn bản quá dài (>100KB)
            preview =
              text.substring(0, 100000) + "...[Nội dung còn lại đã bị cắt bớt]";
            toast.warning(
              `File ${file.name} quá lớn, một phần nội dung có thể bị cắt bớt.`,
              {
                position: "top-right",
                autoClose: 5000,
              }
            );
          } else {
            preview = text;
          }
        }

        return {
          file,
          name: file.name,
          type: fileType,
          preview,
        };
      })
    );

    setContextFiles([...contextFiles, ...processedFiles]);
  } catch (error) {
    console.error("Lỗi khi xử lý file context:", error);
    toast.error(
      "Đã xảy ra lỗi khi xử lý file. Vui lòng thử lại với file nhỏ hơn.",
      {
        position: "top-right",
        autoClose: 5000,
      }
    );
  } finally {
    setIsProcessingContext(false);
  }
};

/**
 * Gợi ý chủ đề cho bài viết
 * @param {string} requirements - Yêu cầu về nội dung bài viết
 * @param {string} selectedProjectType - Loại hình BĐS được chọn
 * @param {string} selectedContentType - Nội dung chủ đạo
 * @param {Object} selectedPublishingPlatform - Nền tảng xuất bản được chọn
 * @param {string} targetCustomer - Đối tượng khách hàng mục tiêu
 * @param {Function} setIsSuggestingTopics - Hàm cập nhật trạng thái gợi ý chủ đề
 * @param {Function} setSuggestedTopics - Hàm cập nhật danh sách chủ đề gợi ý
 * @returns {Promise<void>}
 */
export const suggestTopics = async (
  requirements,
  selectedProjectType,
  selectedContentType,
  selectedPublishingPlatform,
  targetCustomer,
  setIsSuggestingTopics,
  setSuggestedTopics
) => {
  setIsSuggestingTopics(true);

  try {
    let info = "những chủ đề đang hot trên mạng xã hội facebook";

    if (requirements) {
      info = requirements;
    }

    // Thêm thông tin về các trường mới nếu có
    let additionalInfo = "";

    if (selectedProjectType) {
      additionalInfo += `Loại hình BĐS: ${selectedProjectType}\n`;
    }

    if (selectedContentType) {
      additionalInfo += `Nội dung chủ đạo: ${selectedContentType}\n`;
    }

    if (selectedPublishingPlatform) {
      additionalInfo += `Mục đích tạo nội dung: ${
        selectedPublishingPlatform.text || selectedPublishingPlatform
      }\n`;
    }

    // Thêm thông tin về đối tượng khách hàng mục tiêu nếu có
    if (targetCustomer) {
      additionalInfo += `Đối tượng khách hàng mục tiêu: ${targetCustomer}\n`;
    }

    // Kết hợp thông tin
    if (additionalInfo) {
      info = `${additionalInfo}\n${info}`;
    }

    const promptRequest = `
Bạn là một Chuyên gia Sáng tạo Nội dung (Content Strategist & Creator) có tư duy chiến lược và kinh nghiệm đa dạng trên nhiều lĩnh vực, chuyên biến thông tin phức tạp thành những ý tưởng nội dung hấp dẫn và dễ tiếp cận cho khán giả đại chúng trên mạng xã hội.

**Mục tiêu:** Dựa trên thông tin cốt lõi được cung cấp trong \`[THÔNG TIN CUNG CẤP]\`, hãy phân tích sâu và đề xuất **10 chủ đề bài viết** độc đáo, cụ thể, và có khả năng thu hút tương tác cao khi đăng tải (dạng văn bản kèm hình ảnh).

**Yêu cầu Chi tiết:**
1.  **Phân tích & Thấu hiểu:** Nghiên cứu kỹ lưỡng \`[THÔNG TIN CUNG CẤP]\` để xác định các khái niệm, thuật ngữ, đối tượng, hoặc điểm nhấn quan trọng nhất của chủ đề/lĩnh vực đó.
2.  **Tạo Chủ đề Cụ thể & Hoàn chỉnh:**
    *   **Mỗi chủ đề phải là một câu hoàn chỉnh, rõ ràng, và mang tính gợi mở, sẵn sàng để triển khai thành bài viết.**
    *   **Quan trọng:** Thay vì sử dụng các ký hiệu giữ chỗ chung chung (ví dụ: \`[Tên sản phẩm]\`, \`[Khái niệm]\`, \`[Địa điểm]\`, \`[Số liệu]\`, v.v.), hãy **chủ động tích hợp các thuật ngữ, tên riêng, hoặc khái niệm then chốt đã xác định được từ \`[THÔNG TIN CUNG CẤP]\` vào nội dung của từng chủ đề.** Mục tiêu là làm cho mỗi chủ đề trở nên độc lập và có ý nghĩa ngay cả khi đọc riêng lẻ.
3.  **Tập trung vào Bài Viết:** Đảm bảo các chủ đề phù hợp để phát triển thành nội dung dạng **văn bản có kèm hình ảnh minh họa**, tránh các ý tưởng chỉ tối ưu cho video, livestream, hoặc infographic phức tạp.
4.  **Đa dạng Góc nhìn:** Khai thác nhiều khía cạnh khác nhau của thông tin trong \`[THÔNG TIN CUNG CẤP]\`, tránh trùng lặp ý tưởng một cách rõ rệt giữa 10 chủ đề.
5.  **Thu hút & Tương tác:** Ưu tiên các chủ đề có khả năng kích thích sự tò mò, tạo ra thảo luận, hoặc khuyến khích chia sẻ. Cân nhắc các dạng như: giải thích khái niệm khó, so sánh, góc nhìn ít biết, ứng dụng thực tế, câu chuyện liên quan, giải đáp thắc mắc phổ biến,...
6.  **Ngôn ngữ Tự nhiên:** Sử dụng tiếng Việt chuẩn xác, tự nhiên, phù hợp với đối tượng đọc và thuật ngữ chuyên ngành (nếu có) trong \`[THÔNG TIN CUNG CẤP]\`.

**[THÔNG TIN CUNG CẤP]:**
${info}

**Định dạng Kết quả (JSON):**
Chỉ trả về kết quả dưới dạng một đối tượng JSON hợp lệ, **không bao gồm bất kỳ lời dẫn, giải thích, hay định dạng nào khác ngoài cấu trúc JSON được yêu cầu**:
{
  "topics": [
    "Chủ đề cụ thể và hoàn chỉnh 1",
    "Chủ đề cụ thể và hoàn chỉnh 2",
    "Chủ đề cụ thể và hoàn chỉnh 3",
    "Chủ đề cụ thể và hoàn chỉnh 4",
    "Chủ đề cụ thể và hoàn chỉnh 5",
    "Chủ đề cụ thể và hoàn chỉnh 6",
    "Chủ đề cụ thể và hoàn chỉnh 7",
    "Chủ đề cụ thể và hoàn chỉnh 8",
    "Chủ đề cụ thể và hoàn chỉnh 9",
    "Chủ đề cụ thể và hoàn chỉnh 10"
  ]
}
`;

    const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${process.env.REACT_APP_GEMINI_API_KEY}`;

    const data = {
      contents: [
        {
          parts: [
            {
              text: promptRequest,
            },
          ],
        },
      ],
      tools: [
        {
          google_search: {},
        },
      ],
    };

    const headers = {
      "Content-Type": "application/json",
    };

    const response = await axios.post(url, data, { headers });
    const responseData = response.data;
    let jsonData = responseData.candidates[0].content.parts[0].text;

    jsonData = jsonData.replace("```json", "").replace("```", "");
    const rawData = JSON.parse(jsonData);
    setSuggestedTopics(rawData.topics);
  } catch (error) {
    console.error("Error suggesting topics:", error);
    toast.error("Đã xảy ra lỗi khi gợi ý chủ đề. Vui lòng thử lại sau.", {
      position: "top-right",
      autoClose: 5000,
    });
  } finally {
    setIsSuggestingTopics(false);
  }
};

/**
 * Lưu bài viết
 * @param {Object} generatedPost - Bài viết đã tạo
 * @param {string} editedPostText - Nội dung bài viết đã chỉnh sửa
 * @param {Array} selectedSerperImages - Danh sách ảnh từ Serper được chọn
 * @param {string} geminiGeneratedImage - Ảnh được tạo bởi Gemini
 * @param {string} selectedProjectType - Loại hình BĐS được chọn
 * @param {string} selectedContentType - Nội dung chủ đạo
 * @param {Object} selectedPublishingPlatform - Nền tảng xuất bản được chọn
 * @param {string} targetCustomer - Đối tượng khách hàng mục tiêu
 * @param {string} requirements - Yêu cầu về nội dung bài viết
 * @param {string} postLength - Độ dài bài viết
 * @param {Array} postStyles - Phong cách bài viết
 * @param {boolean} includeContactInfo - Có thêm thông tin liên hệ hay không
 * @param {boolean} allowNoImage - Cho phép không dùng ảnh hay không
 * @param {string} imageSource - Nguồn ảnh
 * @param {Function} setIsSaving - Hàm cập nhật trạng thái đang lưu
 * @param {Function} setSavedPostsCount - Hàm cập nhật số lượng bài viết đã lưu
 * @returns {Promise<Object>} - Kết quả lưu bài viết
 */
export const savePost = async (
  generatedPost,
  editedPostText,
  selectedSerperImages,
  geminiGeneratedImage,
  selectedProjectType,
  selectedContentType,
  selectedPublishingPlatform,
  targetCustomer,
  requirements,
  postLength,
  postStyles,
  includeContactInfo,
  allowNoImage,
  imageSource,
  referenceUrl,
  setIsSaving,
  setSavedPostsCount
) => {
  if (!generatedPost) {
    toast.error("Không có bài viết nào để lưu", {
      position: "top-right",
      autoClose: 3000,
    });
    return { success: false, error: "Không có bài viết" };
  }

  setIsSaving(true);

  try {
    // Kiểm tra số lượng bài viết đã lưu
    const savedPostsCount = await countSavedPosts();

    if (savedPostsCount >= 20) {
      toast.error(
        "Bạn đã đạt giới hạn tối đa 20 bài viết đã lưu. Vui lòng xóa bớt bài viết cũ để lưu thêm.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return { success: false, error: "Đã đạt giới hạn bài viết" };
    }

    // Chuẩn bị dữ liệu bài viết để lưu
    const postData = {
      title: generatedPost.title || "Bài viết",
      content: editedPostText || generatedPost.content,
      images:
        selectedSerperImages.length > 0
          ? selectedSerperImages
          : geminiGeneratedImage
          ? [geminiGeneratedImage]
          : [],
      metadata: {
        projectType: selectedProjectType,
        contentType: selectedContentType,
        publishingPlatform: selectedPublishingPlatform,
        targetCustomer: targetCustomer,
        requirements: requirements,
        postLength: postLength,
        postStyles: postStyles,
        includeContactInfo: includeContactInfo,
        allowNoImage: allowNoImage,
        imageSource: imageSource,
        referenceUrl: referenceUrl || "", // Lưu URL tham khảo
      },
      createdAt: Date.now(),
    };

    const result = await savePostToFirebase(postData);

    if (result.success) {
      // Cập nhật lại số lượng bài viết đã lưu
      setSavedPostsCount(savedPostsCount + 1);
      toast.success("Đã lưu bài viết thành công", {
        position: "top-right",
        autoClose: 3000,
      });
    } else {
      toast.error(`Lỗi khi lưu bài viết: ${result.error}`, {
        position: "top-right",
        autoClose: 5000,
      });
    }

    return result;
  } catch (error) {
    console.error("Lỗi khi lưu bài viết:", error);
    toast.error(`Lỗi khi lưu bài viết: ${error.message}`, {
      position: "top-right",
      autoClose: 5000,
    });
    return { success: false, error: error.message };
  } finally {
    setIsSaving(false);
  }
};

/**
 * Kiểm tra plan của người dùng
 * @param {Function} navigate - Hàm điều hướng
 * @returns {Promise<boolean>} - Kết quả kiểm tra
 */
// Note: checkUserPlan function removed - system is now free to use

/**
 * Lấy thông tin fanpage từ localStorage
 * @param {Array} selectedFanpages - Danh sách fanpage được chọn
 * @returns {string} - Thông tin fanpage
 */
export const getFanpageInfo = (selectedFanpages) => {
  // Kiểm tra nếu người dùng đã chọn fanpage
  if (selectedFanpages.length > 0 && selectedFanpages[0].id) {
    // Lấy danh sách fanpage đã lưu
    const storedFanpages = JSON.parse(localStorage.getItem("fanpages") || "{}");
    // Lấy thông tin fanpage cụ thể
    const fanpageData = storedFanpages[selectedFanpages[0].id] || {};
    // Trả về thông tin fanpage nếu có
    return fanpageData.fanpageInfo || "";
  }
  // Nếu không có fanpage được chọn, trả về chuỗi rỗng
  return "";
};

/**
 * Xử lý việc tạo bài viết
 * @param {Object} options - Các tùy chọn cho việc tạo bài viết
 * @param {string} userId - ID người dùng
 * @param {boolean} allowNoImage - Cho phép không dùng ảnh hay không
 * @param {string} imageSource - Nguồn ảnh
 * @param {Array} contextFiles - Danh sách file ngữ cảnh
 * @param {Function} incrementPostCount - Hàm tăng số lượng bài viết
 * @param {Object} refs - Các tham chiếu
 * @param {Function} generateNewPost - Hàm tạo bài viết mới từ hook
 * @returns {Promise<void>}
 */
export const handleGeneratePost = async (
  options,
  userId,
  allowNoImage,
  imageSource,
  contextFiles,
  incrementPostCount,
  refs,
  generateNewPost
) => {
  await generateNewPost(
    options,
    userId,
    allowNoImage,
    imageSource,
    contextFiles,
    incrementPostCount,
    refs
  );
};

/**
 * Xử lý việc tái tạo bài viết
 * @param {Object} options - Các tùy chọn cho việc tạo bài viết
 * @param {string} userId - ID người dùng
 * @param {Function} regeneratePost - Hàm tái tạo bài viết từ hook
 * @returns {Promise<void>}
 */
export const handleRegeneratePost = async (options, userId, regeneratePost) => {
  await regeneratePost(options, userId);
};

/**
 * Xử lý việc tái tạo ảnh
 * @param {string} userId - ID người dùng
 * @param {boolean} allowNoImage - Cho phép không dùng ảnh hay không
 * @param {string} imageSource - Nguồn ảnh
 * @param {Function} regenerateImages - Hàm tái tạo ảnh từ hook
 * @returns {Promise<void>}
 */
export const handleRegenerateImages = async (
  userId,
  allowNoImage,
  imageSource,
  regenerateImages
) => {
  await regenerateImages(userId, allowNoImage, imageSource);
};

/**
 * Tải dữ liệu từ bài viết đã lưu
 * @param {Function} setSelectedProjectType - Hàm cập nhật loại hình BĐS được chọn
 * @param {Function} setSelectedContentType - Hàm cập nhật nội dung chủ đạo
 * @param {Function} setSelectedPublishingPlatform - Hàm cập nhật nền tảng xuất bản
 * @param {Function} setTargetCustomer - Hàm cập nhật đối tượng khách hàng mục tiêu
 * @param {Function} setRequirements - Hàm cập nhật yêu cầu
 * @param {Function} setPostLength - Hàm cập nhật độ dài bài viết
 * @param {Function} setPostStyles - Hàm cập nhật phong cách bài viết
 * @param {Function} setIncludeContactInfo - Hàm cập nhật thông tin liên hệ
 * @param {Function} setAllowNoImage - Hàm cập nhật cho phép không dùng ảnh
 * @param {Function} setImageSource - Hàm cập nhật nguồn ảnh
 * @param {Object} defaults - Các giá trị mặc định
 * @param {Object} PublishingPlatforms - Các nền tảng xuất bản
 * @returns {boolean} - Có dữ liệu được nạp hay không
 */
export const loadSavedPostData = (
  setSelectedProjectType,
  setSelectedContentType,
  setSelectedPublishingPlatform,
  setTargetCustomer,
  setRequirements,
  setPostLength,
  setPostStyles,
  setIncludeContactInfo,
  setAllowNoImage,
  setImageSource,
  setReferenceUrl,
  defaults,
  PublishingPlatforms
) => {
  // Kiểm tra xem có bài viết đã lưu được chọn để chỉnh sửa không
  const savedPost = localStorage.getItem("editSavedPost");
  if (savedPost) {
    try {
      const postData = JSON.parse(savedPost);

      // Nạp dữ liệu từ bài viết đã lưu
      if (postData.metadata) {
        setSelectedProjectType(
          postData.metadata.projectType || defaults.selectedProjectType || ""
        );
        setSelectedContentType(
          postData.metadata.contentType || defaults.selectedContentType || ""
        );

        // Xử lý publishingPlatform
        if (postData.metadata.publishingPlatform) {
          if (typeof postData.metadata.publishingPlatform === "object") {
            setSelectedPublishingPlatform(postData.metadata.publishingPlatform);
          } else if (typeof postData.metadata.publishingPlatform === "string") {
            setSelectedPublishingPlatform({
              text: postData.metadata.publishingPlatform,
              id:
                PublishingPlatforms[postData.metadata.publishingPlatform] || 1,
            });
          }
        }

        setTargetCustomer(postData.metadata.targetCustomer || "");
        setRequirements(postData.metadata.requirements || "");
        setPostLength(
          postData.metadata.postLength ||
            defaults.postLength ||
            DEFAULT_POST_LENGTH
        );
        setPostStyles(
          postData.metadata.postStyles ||
            defaults.postStyles ||
            DEFAULT_POST_STYLES
        );
        setIncludeContactInfo(postData.metadata.includeContactInfo || false);
        setAllowNoImage(postData.metadata.allowNoImage || false);
        setImageSource(postData.metadata.imageSource || "internet");
        setReferenceUrl(postData.metadata.referenceUrl || "");
      }

      // Hiển thị thông báo
      toast.info("Dữ liệu từ bài viết đã lưu đã được nạp", {
        position: "top-right",
        autoClose: 3000,
      });

      // Xóa dữ liệu từ localStorage sau khi đã nạp
      localStorage.removeItem("editSavedPost");

      return true;
    } catch (error) {
      console.error("Lỗi khi nạp dữ liệu bài viết đã lưu:", error);
      localStorage.removeItem("editSavedPost");
    }
  }

  return false;
};
